import React, {ComponentProps, forwardRef} from 'react'
import * as Avatar from '@radix-ui/react-avatar'
import {SxProp, useRefObjectAsForwardedRef} from '../..'
import {AvatarProps, AvatarImageProps, AvatarFallbackProps} from './types'

/**
 *
 * Contains all the parts of an avatar.
 *
 */
const Root = forwardRef(({children, asChild = false, sx: sxProp, ...props}: AvatarProps, forwardedRef) => {
  const innerRef = React.useRef(null) as React.RefObject<HTMLSpanElement>
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <Avatar.Root ref={innerRef} {...props}>
      {children}
    </Avatar.Root>
  )
})

/**
 *
 * The image to render. By default it will only render when it has loaded. You can use the onLoadingStatusChange handler if you need more control.
 *
 */
const Image = ({children, src, alt, asChild = false, ...props}: AvatarImageProps) => {
  // @TODO, check for empty string or alt text and throw error if none passed
  if (!alt) {
    console.error(
      `You must include accessible alt text with avatar images. If the avatar is purely decorative and is described by accompanying text, use an empty string ''. `
    )
  }
  return (
    <Avatar.Image asChild={asChild} src={src} alt={alt} {...props}>
      {children}
    </Avatar.Image>
  )
}

/**
 * An element that renders when the image hasn't loaded. This means that while it's loading, or if there was an error.
 *
 * If you notice a flash during loading, you can provide a delayMs prop to delay its rendering so it only renders for those with slower connections. For more control, use the onLoadingStatusChange handler on Avatar.Image.
 */
const Fallback = ({children, asChild = false, delayMs, ...props}: AvatarFallbackProps) => {
  return (
    <Avatar.Fallback asChild={asChild} delayMs={delayMs} {...props}>
      {children}
    </Avatar.Fallback>
  )
}

Root.displayName = 'Avatar'
Image.displayName = 'Image'
Fallback.displayName = 'Fallback'

export default Object.assign(Root, {Image, Fallback})
